import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import '../styles/screens/ExecutionList.css';
import { fetchData, api, extractApiError } from '../services/api';
import { PATH_SEGMENTS, route } from '../config/paths';
import { useLayout } from '../contexts/LayoutContext';
import { useToast } from '../contexts/ToastContext';
import SummaryTable, { SummaryTableColumn, SummaryTableRow, SummaryItem } from '../components/SummaryTable';
import NavigationTabs from '../components/common/NavigationTabs';
import { formatDateOnly, formatTimeOnly } from '../utils/dateUtils';

interface Execution {
  id: number;
  interaction_id: number;
  transcript_id: number;
  execution_time: string;
  genai_tool: string;
  search_tool: string;
  mmt: boolean;
  eval: boolean;
  adherence: boolean;
  summary: boolean;
  mmts?: Record<string, MomentData>;
}

interface MomentData {
  id: number;
  name: string;
  sentiment: string;
  mmt_category: string;
  speech_text_id: number;
}

interface SpeechText {
  id: number;
  transcript_id: number;
  speech: string;
  start_ms: number;
  end_ms: number;
  duration: number;
  speaker_id: number;
  speaker_name: string;
  speaker_description: string;
}

interface Transcript {
  transcript: {
    id: number;
    name: string;
    time: string | null;
    language: string;
    transcriber: string;
    transcriber_config: string;
    transcription_language: boolean;
    interaction_id: number;
  };
  speech_texts: SpeechText[];
}

interface ExecutionResponse {
  message: string;
  data: {
    executions: Execution[];
    transcript?: Transcript;
    filters?: Record<string, any>;
  };
}

interface MomentRow {
  momentId: number;
  momentName: string;
  timestamp: string;
  speechText: string;
  executions: Record<number, boolean>; // execution id -> exists
}

type ExecutionType = 'mmt' | 'eval' | 'summary' | 'adherence';

const ExecutionList: React.FC = () => {
  const { type } = useParams<{ type: ExecutionType }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { updateHeaderProps } = useLayout();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<ExecutionType>(type || 'mmt');
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const hasFetchedRef = useRef<string | null>(null);
  const headerUpdatedRef = useRef(false);
  const isNavigatingRef = useRef(false);

  // Update header props only once on mount
  useEffect(() => {
    if (!headerUpdatedRef.current) {
      headerUpdatedRef.current = true;
      updateHeaderProps({
        pageTitle: 'Executions',
        showInteractionExId: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  const fetchExecutions = useCallback(async (queryParams?: URLSearchParams) => {
    setLoading(true);
    try {
      if (!queryParams) {
        queryParams = new URLSearchParams();
      }

      const endpoint = `${api(PATH_SEGMENTS.EXECUTION, activeTab)}?${queryParams.toString()}`;
      const response: ExecutionResponse = await fetchData(endpoint);
      
      setExecutions(response.data.executions || []);
      
      if (response.data.transcript) {
        setTranscript(response.data.transcript);
      }
    } catch (err) {
      console.error('Error fetching executions:', err);
      // Extract detailed error message from API response using common function
      const extractedError = await extractApiError(err);
      
      // Show toast with detailed error message
      showToast(extractedError.message, 'error', 5000);
      // Clear data to show "no records" state instead of error state
      setExecutions([]);
      setTranscript(null);
    } finally {
      setLoading(false);
    }
  }, [activeTab, showToast]);

  // Sync activeTab with URL param on mount or when type changes
  useEffect(() => {
    if (type && type !== activeTab) {
      setActiveTab(type as ExecutionType);
    }
  }, [type]); // Only depend on type to prevent loops

  // Update URL when activeTab changes (but skip if URL already matches or we're already navigating)
  useEffect(() => {
    const expectedPath = route(PATH_SEGMENTS.EXECUTION, activeTab);
    // Only navigate if the pathname doesn't match and we're not already navigating
    if (location.pathname !== expectedPath && !isNavigatingRef.current) {
      isNavigatingRef.current = true;
      navigate(`${expectedPath}${location.search}`, { replace: true });
      // Reset the flag after navigation completes
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 0);
    }
  }, [activeTab, navigate, location.pathname]); // Removed location.search to prevent loop

  // Fetch executions when activeTab or location.search changes
  // Use a ref to prevent duplicate calls from React StrictMode double renders
  useEffect(() => {
    // Create a unique key for this fetch based on activeTab and location.search
    const searchString = location.search;
    const fetchKey = `${activeTab}-${searchString}`;
    
    // Skip if we've already fetched for this exact combination
    if (hasFetchedRef.current === fetchKey) {
      return;
    }
    
    hasFetchedRef.current = fetchKey;
    fetchExecutions(new URLSearchParams(searchString));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, activeTab]); // Removed fetchExecutions and showToast to prevent loops

  // Helper function to format milliseconds to timestamp string
  const formatMsToTimestamp = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };

  // Transform executions data for Moments tab
  const transformMomentsData = (): MomentRow[] => {
    if (activeTab !== 'mmt' || !executions.length || !transcript) return [];

    // Create a map of speech_text_id -> speech_text for quick lookup
    const speechTextMap = new Map<number, SpeechText>();
    transcript.speech_texts.forEach(st => {
      speechTextMap.set(st.id, st);
    });

    // Group by speech_text_id and moment ID
    // Key: `${speech_text_id}_${momentId}`, Value: MomentRow
    const rowMap = new Map<string, MomentRow>();

    executions.forEach(execution => {
      if (execution.mmts) {
        Object.values(execution.mmts).forEach(moment => {
          const speechText = speechTextMap.get(moment.speech_text_id);
          if (!speechText) return; // Skip if speech_text not found
          
          const key = `${moment.speech_text_id}_${moment.id}`;
          const timestamp = formatMsToTimestamp(speechText.start_ms);
          
          if (!rowMap.has(key)) {
            rowMap.set(key, {
              momentId: moment.id,
              momentName: moment.name,
              timestamp,
              speechText: speechText.speech,
              executions: {}
            });
          }
          
          const row = rowMap.get(key)!;
          row.executions[execution.id] = true;
        });
      }
    });

    // Convert to array and sort by timestamp (as number for proper sorting), then moment name
    return Array.from(rowMap.values()).sort((a, b) => {
      // Parse timestamp (mm:ss) to seconds for comparison
      const parseTimestamp = (ts: string): number => {
        const [min, sec] = ts.split(':').map(Number);
        return min * 60 + sec;
      };
      
      const aSeconds = parseTimestamp(a.timestamp);
      const bSeconds = parseTimestamp(b.timestamp);
      
      if (aSeconds !== bSeconds) {
        return aSeconds - bSeconds;
      }
      return a.momentName.localeCompare(b.momentName);
    });
  };


  // Build summary items for Moments tab
  const summaryItems = useMemo((): SummaryItem[] => {
    if (activeTab !== 'mmt') return [];
    
    const momentsData = transformMomentsData();
    const totalMoments = momentsData.length;
    const totalExecutions = executions.length;
    const momentsWithExecutions = momentsData.filter(m => 
      Object.values(m.executions).some(exists => exists)
    ).length;

    return [
      {
        id: 'total-moments',
        value: totalMoments,
        icon: <span className="material-symbols-outlined">event</span>,
        tooltip: 'Total moments found',
        className: 'summary-item-primary'
      },
      {
        id: 'total-executions',
        value: totalExecutions,
        icon: <span className="material-symbols-outlined">play_arrow</span>,
        tooltip: 'Total executions',
        className: 'summary-item-secondary'
      },
      {
        id: 'moments-with-executions',
        value: momentsWithExecutions,
        icon: <span className="material-symbols-outlined">check_circle</span>,
        tooltip: 'Moments with executions',
        className: 'summary-item-success'
      }
    ];
  }, [activeTab, executions, transcript]);

  // Build columns for Moments tab
  const buildMomentsColumns = useMemo((): SummaryTableColumn<MomentRow>[] => {
    const columns: SummaryTableColumn<MomentRow>[] = [
      {
        key: 'timestamp',
        label: 'Timestamp',
        width: '1fr',
        sortable: true,
        align: 'left',
        render: (row: MomentRow) => row.timestamp,
        sortAccessor: (row: MomentRow) => {
          const [min, sec] = row.timestamp.split(':').map(Number);
          return min * 60 + sec;
        }
      },
      {
        key: 'speechText',
        label: 'Speech Text',
        width: '3fr',
        sortable: true,
        align: 'left',
        render: (row: MomentRow) => row.speechText,
        sortAccessor: (row: MomentRow) => row.speechText.toLowerCase()
      },
      {
        key: 'momentName',
        label: 'Moment Name',
        width: '2fr',
        sortable: true,
        align: 'left',
        render: (row: MomentRow) => row.momentName,
        sortAccessor: (row: MomentRow) => row.momentName.toLowerCase()
      }
    ];

    // Add a column for each execution
    executions.forEach(execution => {
      const timeStr = formatTimeOnly(execution.execution_time);
      const dateStr = formatDateOnly(execution.execution_time);
      
      columns.push({
        key: `execution_${execution.id}`,
        label: execution.genai_tool,
        subtext: `${dateStr} ${timeStr}`,
        width: '1fr',
        sortable: false,
        align: 'center',
        render: (row: MomentRow) => {
          const exists = row.executions[execution.id] || false;
          return (
            <div style={{ textAlign: 'center' }}>
              {exists ? '✓' : ''}
            </div>
          );
        }
      });
    });

    return columns;
  }, [executions]);

  // Transform moments data to SummaryTable rows
  const momentsRows = useMemo((): SummaryTableRow<MomentRow>[] => {
    const data = transformMomentsData();
    return data.map(moment => ({
      id: `moment-${moment.momentId}-${moment.timestamp}`,
      data: moment
    }));
  }, [executions, transcript, activeTab]);

  const tabs = [
    { id: 'mmt' as ExecutionType, label: 'Moments', icon: 'event', searchable: true },
    { id: 'eval' as ExecutionType, label: 'Evaluation Form', icon: 'description', searchable: false },
    { id: 'summary' as ExecutionType, label: 'Summary', icon: 'summarize', searchable: false },
    { id: 'adherence' as ExecutionType, label: 'Adherence', icon: 'checklist', searchable: false }
  ];

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId as ExecutionType);
    navigate(`${route(PATH_SEGMENTS.EXECUTION, tabId)}${location.search}`, { replace: true });
  };

  if (loading) {
    return <div className="loading">Loading executions...</div>;
  }

  return (
    <div className="execution-list-container">
      <NavigationTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {activeTab === 'mmt' && (
        <SummaryTable
          summaryItems={summaryItems}
          columns={buildMomentsColumns}
          rows={momentsRows}
          defaultSort={{ key: 'timestamp', direction: 'asc' }}
          exportConfig={{
            enabled: true,
            filenameBase: 'executions-moments'
            // Using default CSV export which automatically respects visible columns
          }}
          searchPlaceholderEntity="moments"
          enableColumnCustomization={true}
        />
      )}

      {/* Placeholder for other tabs */}
      {(activeTab === 'eval' || activeTab === 'summary' || activeTab === 'adherence') && (
        <div className="execution-tab-content">
          <p>Tab content for {activeTab} will be implemented here.</p>
        </div>
      )}
    </div>
  );
};

export default ExecutionList;

